/**
 * xo.center visibility hierarchy — derived from the surface classification table.
 * Hierarchy only: no new routes, no content changes.
 */
import type { HubModule, HubProject } from "./xo_hub_data";
import { XO_HUB_MODULES, XO_HUB_PROJECTS } from "./xo_hub_data";
import { isXOCenterHost } from "./xo_center_host";
import type { ContinuityTraversalStepId } from "./continuityTraversalNav";

export type XOCenterLayer = "front_door" | "proof" | "activation" | "operator" | "horizon";

export type XOCenterNavId = "home" | "demo" | "mechanism";

export type XOCenterNavItem = {
  id: XOCenterNavId;
  label: string;
  /** Existing route or home hash anchor — no new paths. */
  href: string;
};

/** First-visit top navigation on xo.center — proof path + mechanism only. */
export const XO_CENTER_FIRST_VISIT_NAV: readonly XOCenterNavItem[] = [
  { id: "home", label: "Home", href: "/" },
  { id: "demo", label: "Demo", href: "/cross-post" },
  { id: "mechanism", label: "Mechanism", href: "/primer/what-xo-is" },
] as const;

/** Canonical proof visit routes — same cinematic shell throughout the journey. */
export function isXOCenterProofVisitPath(pathname: string): boolean {
  const p = pathname.replace(/\/$/, "") || "/";
  if (p === "/cross-post" || p.startsWith("/cross-post/")) return true;
  if (p === "/records/message-bottle-v2" || p.startsWith("/records/")) return true;
  if (p === "/continuity-replay" || p.startsWith("/continuity-replay")) return true;
  if (p === "/call" || p.startsWith("/call/")) return true;
  return false;
}

/** Hide legacy Sidebar on xo.center proof paths (no operator nav chrome). */
/** Top-right nav on proof-visit traversal pages — avoids rail / completion label overlap. */
export function shouldAlignCenterNavTopRight(hostname: string, pathname: string): boolean {
  return isXOCenterSurface(hostname, pathname) && isXOCenterProofVisitPath(pathname);
}

export function isXOCenterVisitorShellPath(hostname: string, pathname: string): boolean {
  return isXOCenterHost(hostname) && isXOCenterProofVisitPath(pathname);
}

const HUB_MODULE_LAYER: Record<string, XOCenterLayer> = {
  call: "activation",
  canonicalls: "front_door",
  vault: "proof",
  ledger: "proof",
  pulse: "activation",
  drops: "activation",
};

const HUB_PROJECT_LAYER: Record<string, XOCenterLayer> = {
  "message-bottle": "activation",
  preparedness: "horizon",
  aether: "horizon",
  "xo-chain": "horizon",
};

/** Operator bucket — existing routes only (not linked from first-visit home body). */
export const XO_CENTER_OPERATOR_LINKS: readonly { label: string; href: string }[] = [
  { label: "Primer Control Room", href: "/primer/control-room" },
  { label: "XO Center manifest", href: "/xo-center/manifest" },
  { label: "Operator console", href: "/operator" },
  { label: "Mission Control", href: "/mission" },
  { label: "XO Watch (diagnostics home)", href: "https://xo.watch/" },
] as const;

/** Horizon bucket — routable but excluded from first-visit navigation. */
export const XO_CENTER_HORIZON_LINKS: readonly { label: string; href: string }[] = [
  { label: "Horizon", href: "/horizon" },
  { label: "Constellation", href: "/constellation" },
  { label: "XO Reserve", href: "/reserve" },
  { label: "XOnvergence", href: "/xonvergence" },
  { label: "Trait Explorer", href: "/traits" },
  { label: "Defense Layer (primer)", href: "/primer/defense-layer" },
] as const;

const TRAVERSAL_HIDDEN_ON_CENTER: ReadonlySet<ContinuityTraversalStepId> = new Set(["symbol", "horizon"]);

export function hubModuleLayer(id: string): XOCenterLayer {
  return HUB_MODULE_LAYER[id] ?? "proof";
}

export function hubProjectLayer(id: string): XOCenterLayer {
  return HUB_PROJECT_LAYER[id] ?? "horizon";
}

export function hubModulesForLayer(layer: XOCenterLayer): readonly HubModule[] {
  return XO_HUB_MODULES.filter((m) => hubModuleLayer(m.id) === layer);
}

export function hubProjectsForLayer(layer: XOCenterLayer): readonly HubProject[] {
  return XO_HUB_PROJECTS.filter((p) => hubProjectLayer(p.id) === layer);
}

/** True on xo.center host or the public hub path (not manifest). */
export function isXOCenterSurface(hostname: string, pathname: string): boolean {
  if (isXOCenterHost(hostname)) return true;
  const p = pathname.replace(/\/$/, "") || "/";
  return p === "/xo-center";
}

export function isXOCenterOperatorPath(pathname: string): boolean {
  const p = pathname.replace(/\/$/, "") || "/";
  return (
    p === "/xo-center/manifest" ||
    p === "/primer/control-room" ||
    p === "/operator" ||
    p.startsWith("/operator/") ||
    p === "/mission" ||
    p === "/dashboard" ||
    p === "/cockpit" ||
    p === "/liquidity-radar" ||
    p === "/pitch" ||
    p.startsWith("/registrar") ||
    p === "/dev/proposal-lifecycle" ||
    p === "/call/dev"
  );
}

export function isXOCenterHorizonPath(pathname: string): boolean {
  const p = pathname.replace(/\/$/, "") || "/";
  return (
    p === "/horizon" ||
    p === "/constellation" ||
    p.startsWith("/constellation/") ||
    p === "/reserve" ||
    p === "/xonvergence" ||
    p === "/terminal" ||
    p === "/traits" ||
    p.startsWith("/traits/") ||
    p === "/casino" ||
    p === "/primer/defense-layer"
  );
}

export function traversalStepVisibleOnCenter(stepId: ContinuityTraversalStepId): boolean {
  return !TRAVERSAL_HIDDEN_ON_CENTER.has(stepId);
}

export function activeCenterNavId(pathname: string, _hash: string): XOCenterNavId {
  const p = pathname.replace(/\/$/, "") || "/";
  if (p === "/" || p === "/xo-center") return "home";
  if (
    p === "/cross-post" ||
    p.startsWith("/cross-post/") ||
    p === "/continuity-replay" ||
    p.startsWith("/continuity-replay") ||
    p === "/records/message-bottle-v2" ||
    p.startsWith("/records/") ||
    p === "/call" ||
    p.startsWith("/call/")
  ) {
    return "demo";
  }
  if (
    p === "/primer/what-xo-is" ||
    p === "/primer/why-systems-fail" ||
    p === "/primer" ||
    p.startsWith("/primer/")
  ) {
    if (p === "/primer/defense-layer") return "home";
    return "mechanism";
  }
  return "home";
}
